import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import Filters, { FilterValues } from '../components/Filters';
import RecommendationPanel from '../components/feed/RecommendationPanel/RecommendationPanel';
import { ROUTES } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import '../styles/manage-recommendations.scss';
import { RecommendationFeedResponse } from '../types/interfaces';
import { apiGet } from '../utils/api';
import { translateEvent } from '../utils/translateEvent';

const FEED_SEED_KEY = 'expertsRecFeedSeed';
const PAGE_SIZE = 25;

const emptyFilters = (): FilterValues => ({
  selectedSport: '',
  selectedLeague: '',
  minOdds: '',
  maxOdds: '',
  minStake: '',
  maxStake: '',
  eventsNumber: '',
  fromDate: '',
  toDate: '',
});

function readSeed(): string {
  if (typeof window === 'undefined') return '';
  let s = sessionStorage.getItem(FEED_SEED_KEY);
  if (!s) {
    s = crypto.randomUUID();
    sessionStorage.setItem(FEED_SEED_KEY, s);
  }
  return s;
}

function buildFeedUrl(
  filters: FilterValues,
  page: number,
  shuffleSeed: string,
  followingOnly: boolean,
): string {
  const p = new URLSearchParams();
  p.set('page', String(page));
  p.set('page_size', String(PAGE_SIZE));
  if (shuffleSeed) p.set('shuffle_seed', shuffleSeed);
  if (filters.selectedSport) p.set('sport_id', filters.selectedSport);
  if (filters.selectedLeague) p.set('league_id', filters.selectedLeague);
  if (filters.minOdds) p.set('min_odds', filters.minOdds);
  if (filters.maxOdds) p.set('max_odds', filters.maxOdds);
  if (filters.minStake) p.set('min_stake', filters.minStake);
  if (filters.maxStake) p.set('max_stake', filters.maxStake);
  if (filters.fromDate) p.set('from_date', filters.fromDate);
  if (filters.toDate) p.set('to_date', filters.toDate);
  if (followingOnly) p.set('following_only', 'true');
  return `/tipsters/recommendations/feed?${p.toString()}`;
}

const Recommendations: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const ft = t.expertsRecommendationsFeed;

  const [filters, setFilters] = useState<FilterValues>(emptyFilters);
  const [followingOnly, setFollowingOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [shuffleSeed, setShuffleSeed] = useState(() => readSeed());
  const [data, setData] = useState<RecommendationFeedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = buildFeedUrl(filters, page, shuffleSeed, followingOnly);
      const res = await apiGet<RecommendationFeedResponse>(url);
      setData(res);
    } catch {
      setError(ft.loadError);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [filters, page, shuffleSeed, followingOnly, ft.loadError]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  const onFiltersChange = (next: FilterValues) => {
    setFilters(next);
    setPage(1);
  };

  const onFiltersClear = () => {
    setPage(1);
    setFollowingOnly(false);
  };

  const onFollowingOnlyChange = (checked: boolean) => {
    if (checked && !isAuthenticated) {
      navigate(`${ROUTES.LOGIN}?redirect=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }
    setFollowingOnly(checked);
    setPage(1);
  };

  const handleShuffle = () => {
    const s = crypto.randomUUID();
    sessionStorage.setItem(FEED_SEED_KEY, s);
    setShuffleSeed(s);
    setPage(1);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const pageLabel = ft.pageOf
    .replace('{page}', String(data?.page ?? page))
    .replace('{totalPages}', String(totalPages));

  return (
    <div className="manage-recs manage-recs--feed">
      <Helmet>
        <title>Expert Picks & Recommendations | PhilipSnat</title>
        <meta name="description" content="Browse free betting picks from all verified expert tipsters on PhilipSnat. Filter by sport, league, and odds to find the best recommendations." />
        <meta property="og:title" content="Expert Picks & Recommendations | PhilipSnat" />
        <meta property="og:description" content="Browse free betting picks from all verified expert tipsters on PhilipSnat. Filter by sport, league, and odds to find the best recommendations." />
        <meta property="og:url" content="https://philipsnat.com/experts/recommendations" />
        <link rel="canonical" href="https://philipsnat.com/experts/recommendations" />
      </Helmet>
      <div className="manage-recs__container">
        <div className="search-experts__filters manage-recs__feed-following-bar" role="search" aria-label={ft.followingFilterAria}>
          <div className="search-experts__filters-toolbar">
            <label className="search-experts__switch">
              <input
                type="checkbox"
                className="sr-only"
                checked={followingOnly}
                onChange={(e) => onFollowingOnlyChange(e.target.checked)}
              />
              <span className="search-experts__switch-track" aria-hidden />
              <span className="search-experts__switch-text">{t.filters.followingTipstersOnly}</span>
            </label>
          </div>
        </div>

        <Filters
          layout="feed"
          onFiltersChange={onFiltersChange}
          onClear={onFiltersClear}
          isDemo={false}
          hideEventsNumber
        />

        <div className="manage-recs__feed-toolbar">
          <button type="button" className="button_secondary" onClick={handleShuffle}>
            {ft.shuffle}
          </button>
        </div>

        {loading && (
          <div className="manage-recs__loading">{t.common.loading}</div>
        )}

        {error && !loading && (
          <div className="manage-recs__empty">{error}</div>
        )}

        {!loading && !error && data && data.items.length === 0 && (
          <div className="manage-recs__empty">{ft.empty}</div>
        )}

        {!loading && !error && data && data.items.length > 0 && (
          <>
            <div className="manage-recs__editorial-grid">
              {data.items.map((item) => (
                <RecommendationPanel
                  key={item.id}
                  item={item}
                  showGameInfo
                  formattedEvent={translateEvent(
                    item.bet_event?.event ?? '',
                    t.eventsDictionary,
                  )}
                />
              ))}
            </div>

            <div className="manage-recs__pagination">
              <button
                type="button"
                className="button_secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {ft.prevPage}
              </button>
              <span className="manage-recs__pagination-label">{pageLabel}</span>
              <button
                type="button"
                className="button_secondary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                {ft.nextPage}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Recommendations;
